import React, { useMemo, useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { submitConnect } from "../lib/api";
import { track } from "../lib/analytics";

/**
 * ConnectForm — "Let's talk" form for investors, potential co-founders,
 * partners and press on the public landing page ("/").
 *
 * Submissions are persisted to MongoDB (`connect_requests`) and are
 * visible to admins via `GET /api/admin/connect`. There is no third
 * party (no Typeform / Tally / Calendly-only) — we own this loop so we
 * can nurture it in-house.
 *
 * All visible copy flows through the `connect.form.*` i18n namespace so
 * FR / ES SSR snapshots ship a fully translated form.
 */
const ConnectForm = ({ initialRole = "investor", messagePlaceholderKey = "connect.form.messagePlaceholder", submitLabelKey = "connect.form.submit" }) => {
  const { t } = useTranslation();

  // Role options live inside the component so the labels re-translate
  // on a Settings → Language switch. The `value` stays a stable enum
  // — backend logs the raw role string regardless of UI locale.
  const ROLE_OPTIONS = useMemo(
    () => [
      { value: "investor", label: t("connect.form.roleInvestor") },
      { value: "cofounder", label: t("connect.form.roleCofounder") },
      { value: "partner", label: t("connect.form.rolePartner") },
      { value: "enterprise", label: t("connect.form.roleEnterprise") },
      { value: "press", label: t("connect.form.rolePress") },
      { value: "other", label: t("connect.form.roleOther") },
    ],
    [t],
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    role: initialRole,
    org: "",
    message: "",
    // Honeypot — bots happily fill hidden fields; humans never see it.
    // Server silently drops requests where this is non-empty.
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    // Minimal client-side checks; server validates again.
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError(t("connect.form.errorRequired"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError(t("connect.form.errorEmail"));
      return;
    }
    setSubmitting(true);
    try {
      await submitConnect(form);
      track("connect_submitted", { role: form.role });
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Server-side `detail` strings come back English by default;
      // for the two error shapes we surface as friendly copy, we
      // ignore the English server string and use the localized
      // fallback so FR/ES users never see "Too many messages…"
      // in English.
      if (status === 429) {
        setError(t("connect.form.errorRateLimit"));
      } else if (typeof detail === "string" && detail) {
        setError(detail);
      } else {
        setError(t("connect.form.errorGeneric"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="bg-white border border-[#EADDD7] rounded-3xl p-10 text-center soft-shadow"
        data-testid="connect-success"
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-[#F2E8E9] flex items-center justify-center mb-5">
          <CheckCircle2 className="h-7 w-7 text-[#9B6A6C]" />
        </div>
        <h3 className="font-serif italic text-2xl text-[#2C2123] mb-2">
          {t("connect.form.successHeadline")}
        </h3>
        <p className="text-[#5C4B51] leading-relaxed max-w-md mx-auto">
          {t("connect.form.successBodyBefore")}{" "}
          <a
            href="mailto:iyal@trubeaute.com"
            className="text-[#9B6A6C] underline-offset-4 hover:underline"
          >
            iyal@trubeaute.com
          </a>{" "}
          {t("connect.form.successBodyAfter")}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border border-[#EADDD7] rounded-3xl p-7 sm:p-9 soft-shadow"
      data-testid="connect-form"
    >
      {/* Honeypot — positioned off-screen, hidden from a11y, autocomplete off.
          Real users never fill it; bots do. Backend silently drops any
          submission where this is non-empty. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-10000px",
          top: "auto",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update("website")}
            data-testid="connect-honeypot-input"
          />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[#8A7D82]">
            {t("connect.form.nameLabel")}
          </span>
          <input
            type="text"
            required
            maxLength={120}
            value={form.name}
            onChange={update("name")}
            className="mt-1.5 w-full rounded-xl border border-[#EADDD7] bg-[#FFFAF7] px-4 py-3 text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors"
            placeholder={t("connect.form.namePlaceholder")}
            data-testid="connect-name-input"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[#8A7D82]">
            {t("connect.form.emailLabel")}
          </span>
          <input
            type="email"
            required
            maxLength={200}
            value={form.email}
            onChange={update("email")}
            className="mt-1.5 w-full rounded-xl border border-[#EADDD7] bg-[#FFFAF7] px-4 py-3 text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors"
            placeholder={t("connect.form.emailPlaceholder")}
            data-testid="connect-email-input"
          />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[#8A7D82]">
            {t("connect.form.roleLabel")}
          </span>
          <select
            value={form.role}
            onChange={update("role")}
            className="mt-1.5 w-full rounded-xl border border-[#EADDD7] bg-[#FFFAF7] px-4 py-3 text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors"
            data-testid="connect-role-select"
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[#8A7D82]">
            {t("connect.form.orgLabel")}{" "}
            <span className="text-[#BBA7AD] normal-case tracking-normal">
              {t("common.optional")}
            </span>
          </span>
          <input
            type="text"
            maxLength={200}
            value={form.org}
            onChange={update("org")}
            className="mt-1.5 w-full rounded-xl border border-[#EADDD7] bg-[#FFFAF7] px-4 py-3 text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors"
            placeholder={t("connect.form.orgPlaceholder")}
            data-testid="connect-org-input"
          />
        </label>
      </div>

      <label className="block mt-4">
        <span className="text-xs uppercase tracking-[0.18em] text-[#8A7D82]">
          {t("connect.form.messageLabel")}
        </span>
        <textarea
          required
          rows={5}
          maxLength={4000}
          value={form.message}
          onChange={update("message")}
          className="mt-1.5 w-full rounded-xl border border-[#EADDD7] bg-[#FFFAF7] px-4 py-3 text-[#2C2123] focus:outline-none focus:border-[#9B6A6C] transition-colors resize-y"
          placeholder={t(messagePlaceholderKey)}
          data-testid="connect-message-input"
        />
      </label>

      {error ? (
        <div
          className="mt-4 text-sm text-[#9B3A3A] bg-[#FFF1F1] border border-[#F3C6C6] rounded-xl px-4 py-3"
          data-testid="connect-error"
        >
          {error}
        </div>
      ) : null}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
        <p className="text-xs text-[#8A7D82] leading-relaxed max-w-md">
          {t("connect.form.privacyNote")}
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-7 py-3.5 text-base transition-all duration-300 shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
          data-testid="connect-submit-btn"
        >
          <Send className="h-4 w-4" />
          {submitting ? t("connect.form.submitting") : t(submitLabelKey)}
        </button>
      </div>
    </form>
  );
};

export default ConnectForm;
